import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ClientInvoiceService } from 'src/app/docs/client-invoice/client-invoice.service';
import { OrgService } from 'src/app/org/org.service';
import { UserService } from 'src/app/services/user.service';
import {
  ClientInvoice,
  ClientInvoiceListRow,
  OrgBankAccount,
  Order,
  ProductInDoc
} from 'src/typings';
import { OrderService } from './order.service';

export interface EnsureOrderInvoiceResult {
  invoice: ClientInvoiceListRow;
  created: boolean;
  paymentDue?: number;
}

export interface OrderPaymentInvoiceTarget {
  invoice: ClientInvoiceListRow;
  paymentDue: number;
}

export interface EnsureOrderPaymentInvoicesResult {
  invoices: OrderPaymentInvoiceTarget[];
  createdInvoiceIds: number[];
}

export type OrderInvoicePurpose = 'payment' | 'shipment';

@Injectable({
  providedIn: 'root'
})
export class OrderInvoiceService {
  constructor(
    private orderService: OrderService,
    private clientInvoiceService: ClientInvoiceService,
    private orgService: OrgService,
    private userService: UserService
  ) {}

  async ensureInvoiceForOrder(
    orderId: number,
    date = new Date(),
    purpose: OrderInvoicePurpose = 'payment'
  ): Promise<EnsureOrderInvoiceResult> {
    const order = await firstValueFrom(this.orderService.getOne(orderId));
    const invoices = await this.getInvoicesByOrder(orderId);
    const uninvoicedProducts = await this.getUninvoicedProducts(order, invoices);

    if (!uninvoicedProducts.length) {
      const existingInvoice = this.findInvoiceByOrder(invoices, purpose);
      if (existingInvoice) {
        return {
          invoice: existingInvoice,
          created: false,
          paymentDue: this.getInvoicePaymentDue(existingInvoice)
        };
      }
    }

    const productsForInvoice = uninvoicedProducts.length
      ? uninvoicedProducts
      : order.products || [];
    const createdInvoice = await this.createInvoiceForOrderProducts(
      order,
      productsForInvoice,
      date
    );

    return {
      invoice: this.mapCreatedInvoiceToListRow(createdInvoice),
      created: true,
      paymentDue: Number(createdInvoice.total)
    };
  }

  async ensureInvoicesForOrderPayment(
    orderId: number,
    date = new Date()
  ): Promise<EnsureOrderPaymentInvoicesResult> {
    const order = await firstValueFrom(this.orderService.getOne(orderId));
    const invoices = await this.getInvoicesByOrder(orderId);
    const uninvoicedProducts = await this.getUninvoicedProducts(order, invoices);
    const createdInvoiceIds: number[] = [];
    let actualInvoices = [...invoices];

    if (!actualInvoices.length || uninvoicedProducts.length) {
      const createdInvoice = await this.createInvoiceForOrderProducts(
        order,
        uninvoicedProducts.length ? uninvoicedProducts : order.products || [],
        date
      );
      actualInvoices = [...actualInvoices, this.mapCreatedInvoiceToListRow(createdInvoice)];
      createdInvoiceIds.push(createdInvoice.id);
    }

    const payableInvoices = actualInvoices
      .map((invoice) => ({
        invoice,
        paymentDue: this.getInvoicePaymentDue(invoice)
      }))
      .filter((target) => target.paymentDue > 0)
      .sort((left, right) => left.invoice.id - right.invoice.id);

    if (!payableInvoices.length) {
      throw new Error('По заказу нет счетов с остатком к оплате');
    }

    return {
      invoices: payableInvoices,
      createdInvoiceIds
    };
  }

  private async getInvoicesByOrder(orderId: number) {
    return firstValueFrom(
      this.clientInvoiceService.search({
        order_id: orderId,
        limit: 100,
        offset: 0
      })
    );
  }

  private findInvoiceByOrder(invoices: ClientInvoiceListRow[], purpose: OrderInvoicePurpose) {
    const sortedInvoices = [...invoices].sort((left, right) => right.id - left.id);

    if (purpose === 'shipment') {
      return (
        sortedInvoices.find((invoice) => invoice.shipment_status === 'UNSHIPPED') ||
        sortedInvoices.find((invoice) => invoice.shipment_status === 'PARTIALLY_SHIPPED') ||
        sortedInvoices[0] ||
        null
      );
    }

    return (
      sortedInvoices.find((invoice) => invoice.payment_status !== 'FULLY_PAID') ||
      sortedInvoices[0] ||
      null
    );
  }

  private getPreferredBankAccount(accounts: OrgBankAccount[]) {
    return accounts.find((account) => account.isPrimary) || accounts[0];
  }

  private async createInvoiceForOrderProducts(order: Order, products: ProductInDoc[], date: Date) {
    const user = await firstValueFrom(this.userService.user);
    const org = user?.defaultOrg;

    if (!org?.id) {
      throw new Error('У пользователя не настроена организация по умолчанию');
    }

    const bankAccounts = await firstValueFrom(this.orgService.getBankAccounts(org.id));
    const orgBankAccount = this.getPreferredBankAccount(bankAccounts);
    if (!orgBankAccount?.id) {
      throw new Error('У организации по умолчанию нет доступного счета');
    }

    const orderId = Number(order.id);
    if (!order.client?.id && !(Number.isInteger(orderId) && orderId > 0)) {
      throw new Error('У заказа не найден клиент');
    }

    if (!products.length) {
      throw new Error('В заказе нет товаров для создания счета');
    }

    return firstValueFrom(
      this.clientInvoiceService.create({
        date,
        order_id: order.id,
        client_id: order.client?.id,
        org_id: org.id,
        org_bank_account_id: orgBankAccount.id,
        products: this.clientInvoiceService.productsToPayload(products)
      })
    );
  }

  private async getUninvoicedProducts(order: Order, invoices: ClientInvoiceListRow[]) {
    const orderProducts = order.products || [];
    if (!invoices.length || !orderProducts.length) {
      return orderProducts;
    }

    const invoiceDetails = await Promise.all(
      invoices.map((invoice) => firstValueFrom(this.clientInvoiceService.getOne(invoice.id)))
    );
    const invoicedQuantityByProductId = invoiceDetails.reduce<Record<number, number>>(
      (accumulator, invoice) => {
        (invoice.items || []).forEach((item) => {
          const productId = Number(item.product?.id);
          if (!productId) {
            return;
          }
          accumulator[productId] = (accumulator[productId] || 0) + Number(item.quan || 0);
        });
        return accumulator;
      },
      {}
    );

    return orderProducts.reduce<ProductInDoc[]>((accumulator, product) => {
      const productId = Number(product.product?.id);
      if (!productId) {
        return accumulator;
      }

      const remainingQuan =
        Number(product.quan || 0) - Number(invoicedQuantityByProductId[productId] || 0);
      if (remainingQuan <= 0) {
        return accumulator;
      }

      accumulator.push({
        ...product,
        quan: remainingQuan,
        sum: remainingQuan * Number(product.price || 0)
      });
      return accumulator;
    }, []);
  }

  private getInvoicePaymentDue(invoice: ClientInvoiceListRow) {
    const collectibleTotal =
      invoice.collectible_total !== undefined && invoice.collectible_total !== null
        ? Number(invoice.collectible_total)
        : Number(invoice.total || 0);
    return Math.max(collectibleTotal - Number(invoice.paid_total || 0), 0);
  }

  private mapCreatedInvoiceToListRow(createdInvoice: ClientInvoice): ClientInvoiceListRow {
    return {
      id: createdInvoice.id,
      date: createdInvoice.date,
      date_created: createdInvoice.date_created,
      total: createdInvoice.total,
      order_id: createdInvoice.order?.id,
      client_id: createdInvoice.client.id,
      user_id: createdInvoice.user.id,
      client_name: createdInvoice.client.name,
      client_surname: createdInvoice.client.surname,
      client_phone: createdInvoice.client.phone || undefined,
      manager: createdInvoice.user.name || '',
      paid_total: 0,
      payment_status: 'UNPAID',
      shipment_status: 'UNSHIPPED'
    };
  }
}
